// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'

// External Module Imports
import { normalizePermissionKey } from '@/modules/role/role.utils'

// Module Imports
import { playerEndpoints } from '@/modules/player/player.constants'
import type {
  TPlayer,
  TSendPlayerNotificationRequest,
  TUpdatePlayerRolesRequest,
} from '@/modules/player/player.types'

type TRawPermission = {
  id?: unknown
  name?: unknown
  description?: unknown
}

type TRawRole = {
  id?: unknown
  name?: unknown
  description?: unknown
  permissions?: unknown
}

type TRawPlayer = {
  id?: unknown
  name?: unknown
  email?: unknown
  password?: unknown
  avatar_id?: unknown
  sign_in_type?: unknown
  terms_and_conditions_accept?: unknown
  email_verified?: unknown
  is_online?: unknown
  last_status_change_at?: unknown
  avatar?: unknown
  role?: unknown
  roles?: unknown
  permissions?: unknown
  game_stats?: unknown
  category_stats?: unknown
  leaderboard_summary?: unknown
}

const mapPermission = (permission: TRawPermission) => {
  const normalizedName = normalizePermissionKey(permission?.name)
  if (typeof permission?.id !== 'string' || !normalizedName) {
    return null
  }

  return {
    id: permission.id,
    name: normalizedName,
    description:
      typeof permission.description === 'string' ? permission.description : '',
  }
}

const mapRole = (role: TRawRole) => {
  if (typeof role?.id !== 'string' || typeof role?.name !== 'string') {
    return null
  }

  const permissions = Array.isArray(role.permissions)
    ? role.permissions
        .map((permission) => mapPermission(permission as TRawPermission))
        .filter(
          (
            permission
          ): permission is NonNullable<ReturnType<typeof mapPermission>> =>
            !!permission
        )
    : []

  return {
    id: role.id,
    name: role.name,
    description: typeof role.description === 'string' ? role.description : '',
    permissions,
  }
}

const mapPlayer = (player: TRawPlayer): TPlayer => {
  const roles = Array.isArray(player.roles)
    ? player.roles
        .map((role) => mapRole(role as TRawRole))
        .filter(
          (role): role is NonNullable<ReturnType<typeof mapRole>> => !!role
        )
    : []

  const permissions = Array.isArray(player.permissions)
    ? player.permissions
        .map((permission) => mapPermission(permission as TRawPermission))
        .filter(
          (
            permission
          ): permission is NonNullable<ReturnType<typeof mapPermission>> =>
            !!permission
        )
    : []

  return {
    id: typeof player.id === 'string' ? player.id : '',
    name: typeof player.name === 'string' ? player.name : '-',
    email: typeof player.email === 'string' ? player.email : '-',
    password: typeof player.password === 'string' ? player.password : '',
    avatar_id: typeof player.avatar_id === 'string' ? player.avatar_id : '',
    sign_in_type:
      typeof player.sign_in_type === 'string' ? player.sign_in_type : '',
    terms_and_conditions_accept:
      typeof player.terms_and_conditions_accept === 'boolean'
        ? player.terms_and_conditions_accept
        : false,
    email_verified:
      typeof player.email_verified === 'boolean'
        ? player.email_verified
        : false,
    is_online: typeof player.is_online === 'boolean' ? player.is_online : false,
    last_status_change_at:
      typeof player.last_status_change_at === 'string'
        ? player.last_status_change_at
        : null,
    avatar: (player.avatar as TPlayer['avatar']) ?? undefined,
    role:
      player.role === 'player' || player.role === 'moderator'
        ? player.role
        : null,
    roles,
    permissions,
    game_stats: (player.game_stats as TPlayer['game_stats']) ?? undefined,
    category_stats:
      (player.category_stats as TPlayer['category_stats']) ?? undefined,
    leaderboard_summary:
      (player.leaderboard_summary as TPlayer['leaderboard_summary']) ??
      undefined,
  }
}

export async function getPlayerList(
  params: TAdminListParams
): Promise<TResponse<TPlayer[]>> {
  const response = await api.get<TResponse<TRawPlayer[]>>(
    playerEndpoints.list(params)
  )

  return {
    ...response.data,
    data: (response.data.data ?? []).map((player) => mapPlayer(player)),
  }
}

export async function getPlayerDetail(id: string): Promise<TPlayer> {
  const response = await api.get<TResponse<TRawPlayer>>(
    playerEndpoints.detail(id)
  )

  return mapPlayer(response.data.data ?? {})
}

export async function getModeratorList(): Promise<TResponse<TPlayer[]>> {
  const response = await api.get<TResponse<TRawPlayer[]>>(
    playerEndpoints.moderatorList()
  )

  return {
    ...response.data,
    data: (response.data.data ?? []).map((player) => mapPlayer(player)),
  }
}

export async function updatePlayerRoles(
  payload: TUpdatePlayerRolesRequest
): Promise<void> {
  await api.put(playerEndpoints.updateRoles(payload.player_id), {
    role_ids: payload.role_ids,
  })
}

export async function sendPlayerNotification(
  payload: TSendPlayerNotificationRequest
): Promise<void> {
  await api.post(playerEndpoints.sendNotification(payload.player_id), {
    title: payload.title,
    message: payload.message,
  })
}
